import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface MainHeaderOverrideContextValue {
  headerOverride: ReactNode | null;
  hideHeaderContainer: boolean;
  setHeaderOverride: (
    node: ReactNode | null,
    options?: { hideContainer?: boolean }
  ) => void;
}

const defaultValue: MainHeaderOverrideContextValue = {
  headerOverride: null,
  hideHeaderContainer: false,
  setHeaderOverride: () => {},
};

const MainHeaderOverrideContext =
  createContext<MainHeaderOverrideContextValue>(defaultValue);

export function MainHeaderOverrideProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [headerOverride, setHeaderOverrideState] = useState<ReactNode | null>(
    null
  );
  const [hideHeaderContainer, setHideHeaderContainer] = useState(false);
  const setHeaderOverride = useCallback(
    (node: ReactNode | null, options?: { hideContainer?: boolean }) => {
      setHeaderOverrideState(() => node);
      setHideHeaderContainer(options?.hideContainer ?? false);
    },
    []
  );
  const value = useMemo(
    () => ({ headerOverride, hideHeaderContainer, setHeaderOverride }),
    [headerOverride, hideHeaderContainer, setHeaderOverride]
  );
  return (
    <MainHeaderOverrideContext.Provider value={value}>
      {children}
    </MainHeaderOverrideContext.Provider>
  );
}

export function useMainHeaderOverride() {
  const context = useContext(MainHeaderOverrideContext);
  if (context === undefined) {
    return defaultValue;
  }
  return context;
}
